import { inject, Injectable } from '@angular/core';
import {
  TeamMember,
  TeamMemberLevel,
  TeamMemberType,
  UpdateTeamMemberPayload,
} from '@team-management/models/team-member';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TeamManagementService } from '@team-management/services/team-management.service';
import { selectSubmission } from '../redux/submission.selectors';
import { Store } from '@ngrx/store';
import { Submission } from '../models';
import { PermissionsService } from '@shared/services/permissions.service';
import {
  PermissionGrants,
  SiteTeam,
  StudyTeam,
  UserRole,
} from '@shared/interfaces/permissions.interface';
import { selectUser } from '@shared/store/selectors/user.selectors';
import { AdminService } from '@admin/admin.service';
import { TEAM_MANAGEMENT_ACTIONS } from '../actions/team.mgt.action';
@Injectable({
  providedIn: 'root',
})
export class TeamMgmtFacadeService {
  private readonly _store = inject(Store);
  private readonly _teamManagementService = inject(TeamManagementService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _adminService = inject(AdminService);
  private readonly _teamMemberFilters = new BehaviorSubject<{
    gridValues: any;
    type: TeamMemberType;
    siteId?: string;
  }>({
    gridValues: {
      limit: 10,
      page: 1,
      searchValue: '',
      filters: null,
      sort_by_field: 'name',
      sort_by_order: 'asc',
    },
    type: 'client',
  });
  private currentUser: any;

  submission$ = this._store.select(selectSubmission).pipe(
    filter((submission): submission is Submission => !!submission),
    distinctUntilChanged(),
  );
  currentUser$ = this._store.select(selectUser).pipe(
    tap((currentUser: any) => {
      this.currentUser = currentUser;
    }),
  );

  clientTeamMemberSearch$ = this.submission$.pipe(
    debounceTime(300),
    distinctUntilChanged((prev, curr) => prev.study?.id === curr.study?.id),
    switchMap((submission) =>
      this._teamMemberFilters.pipe(
        switchMap(({ gridValues }) =>
          this._teamManagementService.loadTeamMembers(
            submission.study?.id ?? '',
            'client',
            gridValues.limit,
            gridValues.page,
            submission.entity_type,
            gridValues.searchValue ?? '',
            submission.site_id,
            gridValues.filters,
            gridValues.sort_by_field,
            gridValues.sort_by_order,
          ),
        ),
      ),
    ),
  );

  wcgTeamMemberSearch$ = this.submission$.pipe(
    debounceTime(300),
    distinctUntilChanged((prev, curr) => prev.study?.id === curr.study?.id),
    switchMap((submission) =>
      this._teamMemberFilters.pipe(
        switchMap(({ gridValues }) =>
          this._teamManagementService.loadTeamMembers(
            submission.study?.id ?? '',
            'wcg',
            gridValues.limit,
            gridValues.page,
            submission.entity_type,
            gridValues.searchValue ?? '',
            submission.site?.id,
            gridValues.filters,
            gridValues.sort_by_field,
            gridValues.sort_by_order,
          ),
        ),
      ),
    ),
  );

  canEdit$: Observable<boolean> = this.submission$.pipe(
    switchMap((submission) => {
      const permissionRequired =
        submission.entity_type === 'study' ? StudyTeam : SiteTeam;
      return this._permissionsService.observeEntityPermissions(
        submission.id,
        [permissionRequired],
        [PermissionGrants.CanEdit],
      );
    }),
    map((canEdit): canEdit is boolean => canEdit ?? false),
  );

  canRemove$: Observable<boolean> = this.submission$.pipe(
    switchMap((submission) => {
      const permissionRequired =
        submission.entity_type === 'study' ? StudyTeam : SiteTeam;
      return this._permissionsService.observeEntityPermissions(
        submission.id,
        [permissionRequired],
        [PermissionGrants.CanRemove],
      );
    }),
    map((canRemove): canRemove is boolean => canRemove ?? false),
  );

  constructor() {
    this.currentUser$.subscribe();
  }

  canRemoveTeamMember(type: TeamMemberType) {
    if (type === 'wcg') {
      return of(true);
    }

    return this.submission$.pipe(
      mergeMap((submission: Submission) => {
        return this._teamManagementService
          .getTotalTeamMembers(
            submission.study?.id ?? '',
            submission.entity_type,
          )
          .pipe(
            mergeMap((totals) => {
              if (totals.client > 1) {
                return of(true);
              }
              return of(false);
            }),
          );
      }),
    );
  }

  searchSubmissionTeamMembers(gridValues: any, type: TeamMemberType) {
    this._teamMemberFilters.next({ gridValues, type });
  }

  removeTeamMember(teamMember: TeamMember) {
    this.submission$.pipe(take(1)).subscribe((submission) => {
      this._teamManagementService.deleteTeamMember(
        submission.study?.id ?? '',
        teamMember,
        submission.entity_type,
        submission.site?.id,
      );
    });
  }
  removeDetailsTeamMember(
    studyId: string,
    teamMember: TeamMember,
    entity_type: any,
    siteId: string,
  ) {
    return this._teamManagementService.deleteTeamMember(
      studyId,
      teamMember,
      entity_type,
      siteId,
    );
  }

  resendInvite(teamMember: TeamMember) {
    this.submission$.pipe(take(1)).subscribe((submission) => {
      this._teamManagementService.resendInvite(
        submission.study?.id ?? '',
        teamMember,
        submission.entity_type,
        submission.site?.id,
      );
    });
  }

  updateTeamMember(payload: UpdateTeamMemberPayload) {
    this.submission$
      .pipe(
        take(1),
        switchMap((submission) => {
          this._teamManagementService.updateTeamMember(
            submission.study?.id ?? '',
            payload,
            submission.entity_type ?? '',
            submission.site?.id ?? '',
          );

          if (
            this.currentUser &&
            payload.teamMember.contact?.user?.id === this.currentUser.id &&
            payload.role_id
          ) {
            return this._adminService.getRoleDetails(payload.role_id).pipe(
              tap((roleDetails: UserRole) => {
                this._permissionsService.setEntityRole(
                  submission.id,
                  roleDetails,
                );
                this.canEdit$.subscribe();
              }),
            );
          }
          return of(null);
        }),
      )
      .subscribe();
  }
  updateTeamMemberAndHandleRole(
    studyId: string,
    payload: UpdateTeamMemberPayload,
    entityType: TeamMemberLevel,
    siteId: string | null,
  ): void {
    this._teamManagementService.updateTeamMember(
      studyId,
      payload,
      entityType,
      siteId ?? '',
    );
    if (
      this.currentUser &&
      payload.teamMember.contact?.user?.id === this.currentUser.id &&
      payload.role_id
    ) {
      this._adminService
        .getRoleDetails(payload.role_id)
        .pipe(
          tap((roleDetails: UserRole) => {
            this._permissionsService.setEntityRole(studyId, roleDetails);
          }),
        )
        .subscribe({
          next: () => console.log('Role update completed'),
          error: (error) => console.error('Error updating role:', error),
        });
    }
  }

  updateTeamSMEQC(data: { user_id: string; attribute: string }, type: string) {
    this.submission$.pipe(take(1)).subscribe((submission) => {
      if (type == 'unassign') {
        this._teamManagementService
          .removeSMEQC(submission.id, data.user_id, data.attribute)
          .subscribe();
      } else {
        this._teamManagementService
          .updateSMEQC(submission.id, data)
          .subscribe();
      }
    });
  }

  checkEmailAddress(email: string) {
    this._store.dispatch(TEAM_MANAGEMENT_ACTIONS.checkEmailAddress({ email }));
  }
}
